import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquare, ShieldAlert, Send, Anchor, ChevronLeft } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

const POLL_MS = 6000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(location.state?.conversationId || null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      return data;
    } catch (e) {
      console.warn("[Messages] list failed:", e?.message);
      return null;
    }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setThread(data.conversation);
      setMessages(data.messages);
      // Mark read (best-effort; badge poll will resync)
      api.post(`/conversations/${id}/read`).catch(() => {});
    } catch (e) {
      console.warn("[Messages] thread failed:", e?.message);
    }
  }, []);

  // Initial load + auto-select first conversation if nothing preselected.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const data = await loadConversations();
      if (cancelled || !data || !data.length) return;
      // Only auto-select on first load, when no explicit choice yet.
      setActiveId((prev) => prev || location.state?.conversationId || data[0].conversation_id);
    })();
    return () => { cancelled = true; };
  }, [user, loadConversations, location.state]);

  useEffect(() => { loadThread(activeId); }, [activeId, loadThread]);

  // Polling — stable identity because both callbacks are dep-free.
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      loadConversations();
      if (activeId) loadThread(activeId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [user, activeId, loadConversations, loadThread]);

  // Auto-scroll to newest
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, activeId]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { body });
      setMessages((prev) => [...prev, data]);
      setDraft("");
      if (data.contact_stripped) {
        toast.warning("Contact info hidden — keep chats on SlipShare for your safety.");
      }
      loadConversations();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading || !user) return <div className="container-editorial py-16">Loading…</div>;

  return (
    <div data-testid="messages-page" className="container-editorial py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-accent mb-2">SlipShare Messages</div>
        <h1 className="font-display text-4xl">Inbox</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Chat with owners and renters. For your safety, phone numbers, emails, and links are automatically hidden.
        </p>
      </div>

      <div className="grid md:grid-cols-[320px,1fr] gap-6 min-h-[560px]">
        {/* Sidebar: conversation list */}
        <aside
          data-testid="messages-sidebar"
          className={`rounded-2xl border border-border bg-card overflow-hidden ${activeId ? "hidden md:block" : ""}`}
        >
          {conversations.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              <MessageSquare className="h-6 w-6 mb-3 text-muted-foreground/60" strokeWidth={1.5} />
              No conversations yet. Message an owner from any dock listing to get started.
            </div>
          ) : (
            <ul>
              {conversations.map((c) => {
                const active = c.conversation_id === activeId;
                return (
                  <li key={c.conversation_id}>
                    <button
                      data-testid={`convo-item-${c.conversation_id}`}
                      onClick={() => setActiveId(c.conversation_id)}
                      className={`w-full text-left px-4 py-4 border-b border-border/60 hover:bg-muted/40 transition-colors ${active ? "bg-muted/60" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-medium truncate">{c.other_user?.name || "Unknown user"}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.last_message_at)}</span>
                      </div>
                      {c.dock?.title && (
                        <div className="text-[11px] uppercase tracking-widest text-accent truncate mb-1">
                          <Anchor className="inline h-3 w-3 mr-1" strokeWidth={1.5}/>{c.dock.title}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground truncate flex-1">{c.last_message || "New conversation"}</span>
                        {c.unread_count > 0 && (
                          <span data-testid={`unread-${c.conversation_id}`} className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-accent text-accent-foreground text-[11px] font-medium px-1.5">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Thread */}
        <section
          data-testid="messages-thread"
          className={`rounded-2xl border border-border bg-card flex flex-col min-h-[560px] ${!activeId ? "hidden md:flex" : "flex"}`}
        >
          {!activeId ? (
            <div className="m-auto text-sm text-muted-foreground">Pick a conversation to start chatting.</div>
          ) : (
            <>
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={() => setActiveId(null)}
                    data-testid="thread-back-btn"
                  >
                    <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                  <div>
                    <div className="font-medium">{thread?.other_user?.name || "…"}</div>
                    {thread?.dock?.title && (
                      <div className="text-[11px] uppercase tracking-widest text-accent">
                        Re: {thread.dock.title}
                      </div>
                    )}
                  </div>
                </div>
                {thread?.dock && (
                  <button
                    onClick={() => navigate(`/docks/${thread.dock.dock_id}`)}
                    className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
                    data-testid="thread-dock-link"
                  >
                    {thread.dock.images?.[0] && (
                      <img src={resolveImageUrl(thread.dock.images[0])} alt="" className="h-8 w-8 rounded object-cover"/>
                    )}
                    View listing →
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-6 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-16">
                    No messages yet. Say hi!
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {timeAgo(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef}></div>
              </div>

              <div className="border-t border-border px-5 py-3 bg-background/50">
                <div className="flex items-start gap-2 text-[11px] text-muted-foreground mb-2">
                  <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" strokeWidth={1.5}/>
                  <span>For your safety, phone numbers, emails, and links are automatically hidden. Keep transactions on SlipShare.</span>
                </div>
                <form
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="flex gap-2"
                >
                  <textarea
                    data-testid="message-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Write a message… (Enter to send)"
                    rows={2}
                    className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/60"
                  />
                  <Button
                    data-testid="send-message-btn"
                    onClick={send}
                    disabled={sending || !draft.trim()}
                    className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground self-end"
                  >
                    <Send className="h-4 w-4" strokeWidth={1.75}/>
                  </Button>
                </form>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
